import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEOHead from '../components/SEOHead';
import { apiFetch } from '../lib/apiClient';
import { scrollTo } from '../utils/smoothScroll';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Hook for reveal-on-scroll
const useRevealOnScroll = () => {
  const [visibleSet, setVisibleSet] = useState(new Set());
  const observer = useRef(null);

  const observe = useCallback((node) => {
    if (!node) return;
    if (!observer.current) {
      observer.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSet((prev) => new Set(prev).add(entry.target.dataset.idx));
              observer.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
    }
    observer.current.observe(node);
  }, []);

  useEffect(() => {
    return () => observer.current?.disconnect();
  }, []);

  return { observe, visibleSet };
};

// Fallback articles
const NICOLA_HERO_IMAGE = '/media/nicola-hero.webp';

const InsightPage = () => {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { observe, visibleSet } = useRevealOnScroll();

  // Scroll to top on mount
  useEffect(() => {
    scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const cacheBuster = `_t=${Date.now()}`;
        const response = await apiFetch(`${API_URL}/api/news?${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setArticles(data);
            setDisplayedArticles(data);
          }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNews();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(articles.map(a => a.category).filter(Boolean))];
  
  // Filter articles
  const filteredArticles = filter === 'all' 
    ? articles 
    : articles.filter(a => a.category === filter);

  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;
    
    // Start fade out
    setIsTransitioning(true);
    
    // After fade out, change filter and fade in
    setTimeout(() => {
      setFilter(newFilter);
      const newFiltered = newFilter === 'all' 
        ? articles 
        : articles.filter(a => a.category === newFilter);
      setDisplayedArticles(newFiltered);
      
      // Small delay before fade in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 250);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <SEOHead page="insight" />
      <Navbar />
      
      {/* Hero */}
      <section className="bg-[#0b114e] pt-32 pb-16 sm:pt-40 sm:pb-24">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/* Left Content */}
            <div className="lg:col-span-2">
              <Link 
                to="/"
                className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna alla home
              </Link>
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Insight.
              </h1>
              <p className="text-white/80 text-lg sm:text-xl max-w-2xl leading-relaxed">
                Strategie, casi studio e riflessioni sul marketing che funziona davvero. 
                Contenuti esclusivi per imprenditori ambiziosi.
              </p>
            </div>
            
            {/* Right - Author Box */}
            <div className="lg:col-span-1">
              <div className="flex items-start gap-4">
                <img
                  src="/media/nicola-hero.webp"
                  alt="Nicola Scerni"
                  className="w-16 h-16 sm:w-20 sm:h-20 min-w-[64px] sm:min-w-[80px] rounded-full object-cover object-top border-2 border-white/20 shadow-lg flex-shrink-0"
                />
                <div>
                  <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                    Nicola Scerni
                  </h3>
                  <p className="text-white/70 text-sm mt-1 leading-relaxed">
                    Consulente di Marketing Strategico. Aiuto imprenditori ambiziosi a dominare il loro mercato con strategie che funzionano.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-100 sticky top-16 sm:top-20 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleFilterChange(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === cat
                    ? 'bg-[#0b114e] text-white'
                    : 'bg-[#f8f9fa] text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'Tutti' : cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#d4883a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">Nessun articolo trovato.</p>
            </div>
          ) : (
            <div 
              className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 transition-opacity duration-300 ease-in-out ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {displayedArticles.map((article, index) => (
                <Link
                  key={article.id || index}
                  to={`/insight/${article.id}`}
                  ref={observe}
                  data-idx={String(index)}
                  className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-[#d4883a]/30 flex flex-col transition-all duration-500 ease-out ${
                    visibleSet.has(String(index))
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: visibleSet.has(String(index)) ? `${(index % 3) * 100}ms` : '0ms' }}
                >
                  {/* Image */}
                  <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-100">
                    <img
                      src={article.imageUrl || article.image_url || article.image || NICOLA_HERO_IMAGE}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: '25% top' }}
                      onError={(e) => { e.target.src = NICOLA_HERO_IMAGE; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    {article.category && (
                      <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-[#0b114e] text-xs font-semibold px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h2 className="text-lg sm:text-xl font-bold text-[#0b114e] mb-3 line-clamp-2 group-hover:text-[#d4883a] transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                      {article.title}
                    </h2>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-gray-400 text-sm">
                        <Clock className="w-4 h-4" />
                        {article.read_time || '5 min'}
                      </span>
                      <span className="text-[#d4883a] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Leggi
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default InsightPage;
